import {
  Ticket,
  BookOpen,
  Mail,
  Megaphone,
  CreditCard,
  UserPlus,
  Globe,
  FileText,
  RefreshCw,
  XCircle,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import {
  useNotificationPreferencesQuery,
  useUpdateNotificationPreferences,
} from "@/lib/api/unified-notifications";

interface TypeConfig {
  type: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Group {
  label: string;
  types: TypeConfig[];
  admin?: boolean;
}

const GROUPS: Group[] = [
  {
    label: "Tickets",
    types: [
      { type: "ticket_created", label: "Created", icon: Ticket },
      { type: "ticket_closed", label: "Closed", icon: XCircle },
      { type: "ticket_status_changed", label: "Status changed", icon: Ticket },
    ],
  },
  {
    label: "Knowledge Base",
    types: [
      { type: "document_created", label: "Document created", icon: FileText },
      { type: "document_updated", label: "Document resynced", icon: RefreshCw },
      { type: "scrape_completed", label: "Scrape completed", icon: Globe },
      { type: "scrape_failed", label: "Scrape failed", icon: Globe },
    ],
  },
  {
    label: "Modmail",
    types: [{ type: "modmail_received", label: "Received", icon: Mail }],
  },
  {
    label: "Other",
    types: [
      {
        type: "subscription_changed",
        label: "Subscription changes",
        icon: CreditCard,
      },
      { type: "team_invite", label: "Team invites", icon: UserPlus },
    ],
  },
  {
    label: "Admin",
    admin: true,
    types: [
      { type: "admin_announcement", label: "Announcements", icon: Megaphone },
      {
        type: "admin_maintenance",
        label: "Maintenance",
        icon: ShieldAlert,
      },
      {
        type: "admin_feature_update",
        label: "Feature updates",
        icon: BookOpen,
      },
      { type: "admin_custom", label: "Custom", icon: Megaphone },
    ],
  },
];

export function NotificationPreferences({ open }: { open: boolean }) {
  const { data: prefs } = useNotificationPreferencesQuery();
  const updatePrefs = useUpdateNotificationPreferences();

  const mutedSet = new Set(prefs?.mutedTypes ?? []);

  function toggle(type: string) {
    const next = new Set(mutedSet);
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }
    updatePrefs.mutate([...next]);
  }

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="overflow-hidden border-b"
        >
          <div className="max-h-[40dvh] space-y-3 overflow-y-auto px-5 py-4">
            <p className="text-muted-foreground text-xs font-medium">
              Mute notification types you don't want to receive.
            </p>
            {GROUPS.map((group) => (
              <div key={group.label}>
                <span className="text-muted-foreground/50 mb-1.5 block font-mono text-[10px] font-semibold uppercase">
                  {group.label}
                </span>
                <div className="space-y-0.5">
                  {group.types.map(({ type, label, icon: Icon }) => {
                    const isMuted = mutedSet.has(type);
                    const isAdmin = !!group.admin;

                    return (
                      <button
                        key={type}
                        type="button"
                        disabled={isAdmin}
                        onClick={() => toggle(type)}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-xs transition-colors ${
                          isAdmin
                            ? "text-muted-foreground/50 cursor-not-allowed"
                            : isMuted
                              ? "text-muted-foreground hover:bg-muted/30"
                              : "text-foreground hover:bg-rose-500/5"
                        }`}
                      >
                        <Icon className="size-3.5 shrink-0" />
                        <span className="flex-1">{label}</span>
                        {isAdmin ? (
                          <span className="text-muted-foreground/40 font-mono text-[10px]">
                            always on
                          </span>
                        ) : isMuted ? (
                          <XCircle className="text-muted-foreground/40 size-3.5" />
                        ) : (
                          <CheckCircle2 className="size-3.5 text-rose-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
